import { api } from './client';
import type {
  InsurancePolicyParty,
  InsurancePolicyPartyInput,
  InsurancePolicyPartyRole,
  Page,
  WriteResult,
} from './types';
import { UUID } from './types';

async function writeWithMaker<T>(
  call: () => Promise<{ data: T; status: number; headers: Headers }>,
): Promise<WriteResult<T>> {
  const r = await call();
  return {
    entity: r.data,
    pending: r.headers.get('X-Change-Request-Status') === 'pending',
    changeRequestId: r.headers.get('X-Change-Request-Id'),
  };
}

export const insurancePolicyPartiesApi = {
  async create(input: InsurancePolicyPartyInput): Promise<WriteResult<InsurancePolicyParty>> {
    return writeWithMaker(() => api.postMeta('/insurance-policy-parties', input));
  },

  async list(
    filters?: {
      policy_id?: UUID;
      person_id?: UUID;
      /** #46 (2026-06-12): multi-select (repeated keys, C-132). */
      role?: InsurancePolicyPartyRole[];
      include_deleted?: boolean;
      /** #39 (2026-06-11): the Deleted bucket alone (two-bucket pills). */
      only_deleted?: boolean;
      person_q?: string;
      policy_q?: string;
      designation?: string[];
      /** P41: a column key, '-' prefix to reverse (server-side). */
      sort?: string;
    },
    limit = 50,
    offset = 0,
  ): Promise<Page<InsurancePolicyParty>> {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (filters?.policy_id) params.append('policy_id', filters.policy_id);
    if (filters?.person_id) params.append('person_id', filters.person_id);
    if (filters?.role) for (const r of filters.role) params.append('role', r);
    if (filters?.include_deleted) params.append('include_deleted', 'true');
    // BUG FIX #46 (2026-06-12): only_deleted was typed here since #39 but
    // NEVER serialised — the Deleted-only bucket silently sent no param
    // (C-131 dead-control class, one layer deeper: typed-but-dropped).
    if (filters?.only_deleted) params.append('only_deleted', 'true');
    if (filters?.person_q) params.append('person_q', filters.person_q);
    if (filters?.policy_q) params.append('policy_q', filters.policy_q);
    if (filters?.designation) for (const d of filters.designation) params.append('designation', d);
    if (filters?.sort) params.append('sort', filters.sort);
    return api.get(`/insurance-policy-parties?${params}`);
  },

  async get(id: UUID): Promise<InsurancePolicyParty> {
    return api.get(`/insurance-policy-parties/${id}`);
  },

  async update(
    id: UUID,
    input: Partial<InsurancePolicyPartyInput>,
  ): Promise<WriteResult<InsurancePolicyParty>> {
    return writeWithMaker(() => api.patchMeta(`/insurance-policy-parties/${id}`, input));
  },

  async delete(id: UUID): Promise<WriteResult<InsurancePolicyParty>> {
    return writeWithMaker(() => api.deleteMeta(`/insurance-policy-parties/${id}`));
  },

  async restore(id: UUID): Promise<WriteResult<InsurancePolicyParty>> {
    return writeWithMaker(() => api.postMeta(`/insurance-policy-parties/${id}/restore`));
  },
};
